import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { useState } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";

import { Button } from "../../../../../Components/Button";
import { ReactComponent as RemixBadge } from "../../../../../../assets/images/badges/remix-icon.svg";
import { StudioSession } from "../../../../../State/studioSession";
import { Moment } from "MVVM/Components/QuickEdit/Moment";
import { analytics } from "MVVM/Analytics";
import { FinalTouches } from "MVVM/Components/QuickEdit/FinalTouches";
import { QuickEditViewModel } from "MVVM/Components/QuickEdit/ViewModel";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import {
  isAtClipLimit,
  isClipLimitEnforced as checkIsClipLimitEnforced,
} from "MVVM/Utilities/ClipLimits";

export const RemixHeader = ({
  remixData,
  remixedCards,
  postRemix,
  hidden,
}: any) => {
  const [studioSession, setStudioSession] = useRecoilState(StudioSession);
  const [isFinalTouchesOpen, setIsFinalTouchesOpen] = useState<boolean>(false);
  const { mediaUploadUrl } = QuickEditViewModel(remixData.moment.id, true);
  const history = useHistory();
  const { allstarUser } = useUser();
  const isClipLimitEnforced = checkIsClipLimitEnforced(
    allstarUser?.decoded?.sub,
  );

  const {
    portalOpen: eligibilityModalOpen,
    setPortalOpen: setEligibilityModalOpen,
    Portal: EligibilityModalPortal,
  } = usePortal(
    <ClipEligibilityModal
      clipQuota={allstarUser?.user?.clipQuota}
      userGroup={allstarUser?.user?.userGroup?.type}
      onClose={() => setEligibilityModalOpen(false)}
    />,
  );

  const handlePostRemix = () => {
    if (isClipLimitEnforced && isAtClipLimit(allstarUser?.user?.clipQuota)) {
      setEligibilityModalOpen(true);
      return;
    }
    postRemix();
  };

  if (!remixData.moment) {
    return null;
  }
  if (hidden) return <></>;

  const handleToggle = (values: string[]) => {
    setStudioSession({
      ...studioSession,
      selectedFinalTouches: values,
    });
  };

  const handleToggleThumb = (value: string) => {
    setStudioSession({
      ...studioSession,
      thumbnailKey: value,
    });
  };

  return (
    <StyledPage justifyContent="space-between">
      <StyledRemix
        flexDirection={["column", "column", "column", "column"]}
        justifyContent="space-between"
      >
        <Flex
          flexDirection={["column"]}
          justifyContent="center"
          height={"100%"}
        >
          <CollapsibleContainer>
            <ToggleButton
              onClick={() => setIsFinalTouchesOpen(!isFinalTouchesOpen)}
              isOpen={isFinalTouchesOpen}
            >
              <Flex
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Flex flexDirection="row" alignItems="center">
                  Add Finishing Touches{" "}
                  <Box style={{ fontSize: 12, opacity: 0.7, marginLeft: 6 }}>
                    {studioSession.selectedFinalTouches.length > 0 &&
                      `(${studioSession.selectedFinalTouches.length})`}
                  </Box>
                </Flex>

                {isFinalTouchesOpen ? (
                  <FaMinus size={12} />
                ) : (
                  <FaPlus size={12} />
                )}
              </Flex>
            </ToggleButton>

            <CollapsibleContent isOpen={isFinalTouchesOpen}>
              <FinalTouches
                setThumbnailKey={handleToggleThumb}
                selectedFinalTouches={studioSession.selectedFinalTouches}
                setSelectedFinalTouches={handleToggle}
                mediaUploadUrl={mediaUploadUrl}
                isOwnMoment={false}
                moment={remixData.moment}
                hideSignature
                disablePreferredTouches
              />
            </CollapsibleContent>
          </CollapsibleContainer>

          <Moment moment={remixData.moment} hideTimelineLabel />

          <Flex style={{ gap: 12 }} justifyContent={"center"} mt={4}>
            <Button
              classification="roundedFilledAlt"
              size="small"
              maxHeight={32}
              onClick={() => {
                analytics.track("Quick Edit Custom Flow - Back", {});
                history.goBack();
              }}
              flex={true}
            >
              Back
            </Button>
            {remixData.moment.clip ? (
              <StyledRemixButton
                disabled={!remixedCards.length && !studioSession.useSignature}
                classification="remixFeed"
                alignSelf={"center"}
                onClick={handlePostRemix}
                maxHeight={32}
              >
                <StyledIcon />
                Remix Clip
              </StyledRemixButton>
            ) : (
              <Button
                flex={true}
                disabled={!remixedCards.length && !studioSession.useSignature}
                classification="roundedFilled"
                alignSelf={"center"}
                size="tiny"
                onClick={handlePostRemix}
              >
                Create Clip
              </Button>
            )}
          </Flex>
        </Flex>
      </StyledRemix>
      {eligibilityModalOpen && EligibilityModalPortal}
    </StyledPage>
  );
};

export const StyledRemixButton = styled(Button)`
  align-items: center;
  flex-basis: content;
  font-size: 16px;
  line-height: 1.9;
  padding-left: 35px;
  padding-right: 35px;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: .75;
    border-color: transparent;
  `}
`;

export const StyledIcon = styled(RemixBadge)`
  flex-shrink: 0;
  height: 18px;
  margin-right: 8px;
  width: 18px;
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    margin-right: 5px;
    height: 14px;
    width: 14px;
  }
`;

const CollapsibleContainer = styled.div`
  width: 100%;
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  background-color: ${(props) => props.theme.colors.grey825};
  border: 1px solid ${(props) => props.theme.colors.ned};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${(props) => props.theme.colors.ice};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background-color: ${(props) => props.theme.colors.grey800};
    border-color: ${(props) => props.theme.colors.midnight};
  }

  &:focus {
    outline: none;
  }
`;

const CollapsibleContent = styled.div<{ isOpen: boolean }>`
  max-height: ${(props) => (props.isOpen ? "1000px" : "0")};
  overflow: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: max-height 0.3s ease-in-out;
  border-radius: 8px;
  margin-top: 8px;
  margin-bottom: ${(props) => (props.isOpen ? "16px" : "0")};
`;

const StyledRemix = styled(Flex)`
  background-color: ${(props) => props.theme.colors.midnight};
  border-color: ${(props) => props.theme.colors.alert};
  border-radius: 0;
  border-top: 2px solid;
  bottom: 0;
  color: ${(props) => props.theme.colors.ice};
  margin: auto;
  padding: 15px;
  position: fixed;
  right: 0;
  width: 100%;
  z-index: 8;
`;

const StyledPage = styled(Flex)`
  justify-content: space-between;
  z-index: 10000;
`;
